import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '../../api/errors';
import { ordersService } from '../../services';
import type { AdminOrderRequest, OrderRequestStatus } from '../../types';
import { formatAmount } from '../../utils/formatPrice';
import { formatOrderNumber } from '../../utils/formatOrderNumber';
import {
  ORDER_REQUEST_STATUS_LABELS,
  ORDER_REQUEST_TYPE_LABELS,
} from '../../utils/orderRequests';

type StatusFilter = 'all' | OrderRequestStatus;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRequestOrderNumber(request: AdminOrderRequest) {
  return formatOrderNumber({ id: request.orderId, orderNumber: request.orderNumber });
}

export default function AdminOrderRequests() {
  const [requests, setRequests] = useState<AdminOrderRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await ordersService.fetchAdminOrderRequests(
        statusFilter === 'all' ? undefined : statusFilter,
      );
      setRequests(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Talepler yüklenemedi.'));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const handleUpdateRequest = async (
    requestId: string,
    status: 'approved' | 'rejected',
  ) => {
    const note = adminNotes[requestId]?.trim() || null;
    const actionLabel = status === 'approved' ? 'onaylamak' : 'reddetmek';
    const confirmed = confirm(`Bu talebi ${actionLabel} istediğinize emin misiniz?`);
    if (!confirmed) return;

    setUpdatingId(requestId);
    setError('');
    try {
      await ordersService.updateAdminOrderRequest(requestId, status, note);
      await loadRequests();
    } catch (err) {
      setError(getErrorMessage(err, 'Talep güncellenemedi.'));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>İptal / İade Talepleri</h1>
          <p>Müşteri iptal ve iade taleplerini yönetin</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={() => void loadRequests()}>
          Yenile
        </button>
      </div>

      <div className="admin-order-filters">
        <label htmlFor="requestStatusFilter">Durum</label>
        <select
          id="requestStatusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">Tümü</option>
          <option value="pending">Bekliyor</option>
          <option value="approved">Onaylandı</option>
          <option value="rejected">Reddedildi</option>
          <option value="withdrawn">Geri çekildi</option>
        </select>
        {statusFilter !== 'all' && (
          <span className="admin-order-filter-count">
            {requests.length} sonuç
          </span>
        )}
      </div>

      {error && (
        <div className="auth-error">
          {error}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadRequests()}>
            Tekrar Dene
          </button>
        </div>
      )}

      {isLoading ? (
        <p>Yükleniyor...</p>
      ) : requests.length === 0 ? (
        <div className="admin-empty">
          <p>
            {statusFilter === 'pending'
              ? 'Bekleyen iptal veya iade talebi yok.'
              : 'Bu filtreye uygun talep bulunamadı.'}
          </p>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table admin-order-requests-table">
            <thead>
              <tr>
                <th>Talep</th>
                <th>Sipariş / Müşteri</th>
                <th>Detay</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const isUpdating = updatingId === request.id;
                const isPending = request.status === 'pending';

                return (
                  <tr key={request.id}>
                    <td>
                      <div className="admin-order-id-cell">
                        <strong>{ORDER_REQUEST_TYPE_LABELS[request.type]}</strong>
                        <span className={`order-status status-request-${request.status}`}>
                          {ORDER_REQUEST_STATUS_LABELS[request.status]}
                        </span>
                        <span>{formatDate(request.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="admin-order-customer">
                        <strong>{request.customerName}</strong>
                        <span>{request.customerEmail}</span>
                        <Link to="/admin/orders" className="admin-link-muted">
                          Sipariş {formatRequestOrderNumber(request)}
                        </Link>
                        <span>{formatAmount(request.orderTotal, request.orderCurrency)}</span>
                      </div>
                    </td>
                    <td>
                      <p className="admin-order-request-reason">{request.reason}</p>
                      {request.type === 'return' && request.photoCount != null && request.photoCount > 0 && (
                        <p className="admin-order-meta">{request.photoCount} fotoğraf yüklendi</p>
                      )}
                      {request.adminNote && (
                        <p className="admin-order-meta">Admin notu: {request.adminNote}</p>
                      )}
                      {request.resolvedAt && (
                        <p className="admin-order-meta">Çözüldü: {formatDate(request.resolvedAt)}</p>
                      )}
                    </td>
                    <td>
                      {isPending ? (
                        <div className="admin-order-request-actions">
                          <textarea
                            rows={2}
                            placeholder="Admin notu (opsiyonel)"
                            value={adminNotes[request.id] ?? ''}
                            disabled={isUpdating}
                            onChange={(event) =>
                              setAdminNotes((prev) => ({
                                ...prev,
                                [request.id]: event.target.value,
                              }))
                            }
                          />
                          <div className="admin-order-request-buttons">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={isUpdating}
                              onClick={() => void handleUpdateRequest(request.id, 'approved')}
                            >
                              {isUpdating ? 'Kaydediliyor...' : 'Onayla'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              disabled={isUpdating}
                              onClick={() => void handleUpdateRequest(request.id, 'rejected')}
                            >
                              Reddet
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="admin-order-meta">
                          {ORDER_REQUEST_STATUS_LABELS[request.status]}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
